'use client'; 
import Link from 'next/link';
import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import AuthButtons from '../AuthButtons';
import { useSignalScore } from '@/lib/useSignalScore';
import { supabaseBrowser } from '@/lib/supabaseBrowser';
import SignalPlant from './SignalPlant';

export default function Nav() {
  const { score, ratingsCount } = useSignalScore();
  const show = score !== null;
  const safeScore = typeof score === 'number' ? score : 0;
  const safeRatingsCount = typeof ratingsCount === 'number' ? ratingsCount : 0;
  const pathname = usePathname();

  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  const [menuOpen, setMenuOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);

  // Clicking a nav link to the page you're already on is a no-op to Next's
  // router -- no navigation happens, nothing visibly changes, so it just
  // looks broken/unresponsive. This intercepts that specific case and gives
  // a small, deliberate signal (smooth scroll to top) instead of silence.
  function handleNavClick(href: string, e: React.MouseEvent, andThen?: () => void) {
    if (pathname === href) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    andThen?.();
  }

  // Escape-to-close for the mobile hamburger panel and both dropdowns
  // (Guide, Legal) — same reasoning as the dashboard modal on the homepage:
  // without this, a keyboard-only user has no way to dismiss any of them.
  useEffect(() => {
    if (!menuOpen && !guideOpen && !legalOpen) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setGuideOpen(false);
        setLegalOpen(false);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [menuOpen, guideOpen, legalOpen]);

  useEffect(() => {
    const saved = localStorage.getItem('ng-theme') as 'dark' | 'light' | null;
    const initial = saved || 'dark';
    setTheme(initial);
    document.body.classList.remove('dark', 'light');
    document.body.classList.add(initial);
  }, []);

  // Logs one session_start event per browser session (not per page load --
  // Nav is mounted once in the root layout and persists across client-side
  // navigation, so this effect only re-fires on a hard reload/new tab; the
  // sessionStorage guard collapses that down to once per actual visit,
  // which is what "how frequently does a user come back" actually needs).
  // Fire-and-forget: a failed/slow log call should never affect the page.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem('ng-session-logged')) return;
    sessionStorage.setItem('ng-session-logged', '1');

    (async () => {
      const supabase = supabaseBrowser();
      const { data: { session } } = await supabase.auth.getSession();

      // Closes the loop with share_clicked -- that tells you a link was
      // shared to, say, Reddit; this tells you whether anyone actually
      // arrived because of it. document.referrer catches organic/social
      // clicks; UTM params catch anything intentionally tagged (e.g. a
      // link posted with ?utm_source=reddit).
      const params = new URLSearchParams(window.location.search);
      const referrer = document.referrer || null;
      const utmSource = params.get('utm_source');
      const utmMedium = params.get('utm_medium');
      const utmCampaign = params.get('utm_campaign');
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      const deviceType = isMobile ? 'mobile' : 'desktop';

      try {
        await fetch('/api/log-event', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
          body: JSON.stringify({
            eventType: 'session_start',
            metadata: { referrer, utmSource, utmMedium, utmCampaign, deviceType },
          }),
        });
      } catch {
        // Non-fatal -- a missed session_start log is not worth surfacing to the user.
      }
    })();
  }, []);

  // The menu stays mounted across client-side navigation (Nav lives in the root
  // layout), so it has to be closed explicitly on route change or it'd still be
  // open after tapping a link inside it.
  function closeMenu() {
    setMenuOpen(false);
  }

  function toggleTheme() {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    document.body.classList.remove('dark', 'light');
    document.body.classList.add(next);
    localStorage.setItem('ng-theme', next);
  }

  const navLink = {
    color: 'var(--text-muted)', fontSize: '13px',
    textDecoration: 'none', transition: 'color 0.2s'
  };

  const mobileNavLink = {
    color: 'var(--text)', fontSize: '15px',
    textDecoration: 'none', padding: '10px 4px',
    borderBottom: '1px solid var(--border-soft)',
  };

  // Now a Link to /garden instead of a static div -- the badge is the
  // entry point into the garden page. group/relative still drive the
  // hover tooltip exactly as before.
  const SignalBadge = (
    <Link href="/garden" className="group relative" onClick={e => handleNavClick('/garden', e, closeMenu)} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px',
      borderRadius: '20px', padding: '4px 10px', fontSize: '12px', fontWeight: 500, cursor: 'pointer',
      background: 'var(--green-glow)', border: '1px solid var(--green-dim)', color: 'var(--green)',
      textDecoration: 'none' }}>
      <SignalPlant score={safeScore} ratingsCount={safeRatingsCount} variant="badge" />
      <span>{Math.round(safeScore)}</span>
      <div className="pointer-events-none absolute -bottom-10 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
        <div style={{ background: 'var(--surface-raised)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}
          className="whitespace-nowrap rounded-md px-2 py-1 text-[10px] shadow-lg">
          View your garden →
        </div>
      </div>
    </Link>
  );

  const ThemeToggle = (
    <button onClick={toggleTheme}
      style={{ background: 'var(--surface-raised)', border: '1px solid var(--border)',
        color: 'var(--text-muted)', borderRadius: '20px', padding: '4px 10px',
        fontSize: '12px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
        transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '4px' }}>
      {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
    </button>
  );

  return (
    <header style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}
      className="w-full backdrop-blur sticky top-0 z-50">
      <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">

        <Link href="/" style={{ color: 'var(--text)' }}
          className="flex items-center gap-2 font-serif text-lg tracking-tight">
          <span style={{ display: 'inline-block', animation: 'sway 4s ease-in-out infinite', transformOrigin: 'bottom center' }}>
            🌿
          </span>
          <span style={{ fontFamily: 'Instrument Serif, serif' }}>NoiseGate</span>
        </Link>

        {/* Full nav row — desktop only */}
        {/* Full nav row — sm (640px) and up. flexWrap is a safety net: on
            tighter landscape phones (iPhone SE-class, ~667px), the worst-case
            logged-in row (badge + Settings + everything else) can genuinely
            exceed the available width — roughly 719px needed vs 667px
            available. Rather than gamble on a breakpoint that's guaranteed
            safe for every device, this lets it wrap to a second line on the
            tightest cases instead of overflowing or cutting off. */}
        <nav className="hidden sm:flex items-center gap-3" style={{ flexWrap: 'wrap', rowGap: '8px' }}>
          <div
            className="relative"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}
            onMouseEnter={() => setGuideOpen(true)}
            onMouseLeave={() => setGuideOpen(false)}
          >
            {/* Plain toggle now, not a Link -- it used to also link straight
                to /info, which duplicated the dropdown's own "How it works"
                link right next to it (WAVE's redundant-link check: two links,
                same destination, adjacent). Restoring "How it works" inside
                the dropdown below instead of removing it, since that's what
                actually solved the original discoverability problem (people
                hovering only ever saw Features/Methodology and never
                realized Guide itself went anywhere) -- this way there's
                exactly one link to /info, and it's the same prominent first
                item it always was. */}
            <span
              role="button"
              tabIndex={0}
              onClick={() => setGuideOpen(o => !o)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setGuideOpen(o => !o); } }}
              style={{ ...navLink, cursor: 'pointer' }}
              onMouseEnter={e => (e.currentTarget.style.color = 'var(--text)')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted)')}>
              Guide
            </span>
            {/* Separate real button, not nested inside the Link above (invalid HTML
                otherwise) — this is what actually makes the dropdown work on touch.
                The old version relied purely on CSS :hover, which doesn't exist on
                phones — tapping just navigated straight through with no way to ever
                see Features/Methodology. This toggles on tap, and still opens on
                mouse-hover for desktop via the wrapping div above. */}
            <button
              onClick={() => setGuideOpen(o => !o)}
              aria-label={guideOpen ? 'Close guide menu' : 'Open guide menu'}
              aria-expanded={guideOpen}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '11px', flexShrink: 0,
              }}
            >
              <svg
                width="10" height="10" viewBox="0 0 24 24" fill="none"
                className={`transition-transform duration-150 ${guideOpen ? 'rotate-180' : ''}`}
                style={{ opacity: 0.6 }}
              >
                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div
              className="absolute top-full left-0 pt-2 transition-opacity duration-150 z-10"
              style={{ opacity: guideOpen ? 1 : 0, pointerEvents: guideOpen ? 'auto' : 'none' }}
            >
              <div style={{
                background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px',
                padding: '6px', display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '140px',
                boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
              }}>
                <Link href="/info" onClick={e => handleNavClick('/info', e, () => setGuideOpen(false))} style={{ color: 'var(--text)', fontWeight: 600, fontSize: '13px', textDecoration: 'none', padding: '7px 10px', borderRadius: '6px', borderBottom: '1px solid var(--border-soft)', marginBottom: '2px', transition: 'background 0.15s' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--surface-raised)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
                  How it works
                </Link>
                <Link href="/demo" onClick={e => handleNavClick('/demo', e, () => setGuideOpen(false))} style={{ color: 'var(--text-muted)', fontSize: '13px', textDecoration: 'none', padding: '7px 10px', borderRadius: '6px', transition: 'background 0.15s' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--surface-raised)'; (e.currentTarget as HTMLElement).style.color = 'var(--text)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)'; }}>
                  Features
                </Link>
                <Link href="/methodology" onClick={e => handleNavClick('/methodology', e, () => setGuideOpen(false))} style={{ color: 'var(--text-muted)', fontSize: '13px', textDecoration: 'none', padding: '7px 10px', borderRadius: '6px', transition: 'background 0.15s' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--surface-raised)'; (e.currentTarget as HTMLElement).style.color = 'var(--text)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)'; }}>
                  Methodology
                </Link>
              </div>
            </div>
          </div>
          <div
            className="relative"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}
            onMouseEnter={() => setLegalOpen(true)}
            onMouseLeave={() => setLegalOpen(false)}
          >
            <span
              role="button"
              tabIndex={0}
              onClick={() => setLegalOpen(o => !o)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setLegalOpen(o => !o); } }}
              style={{ ...navLink, cursor: 'pointer' }}
              onMouseEnter={e => (e.currentTarget.style.color = 'var(--text)')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted)')}>
              Legal
            </span>
            <button
              onClick={() => setLegalOpen(o => !o)}
              aria-label={legalOpen ? 'Close legal menu' : 'Open legal menu'}
              aria-expanded={legalOpen}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '11px', flexShrink: 0,
              }}
            >
              <svg
                width="10" height="10" viewBox="0 0 24 24" fill="none"
                className={`transition-transform duration-150 ${legalOpen ? 'rotate-180' : ''}`}
                style={{ opacity: 0.6 }}
              >
                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div
              className="absolute top-full left-0 pt-2 transition-opacity duration-150 z-10"
              style={{ opacity: legalOpen ? 1 : 0, pointerEvents: legalOpen ? 'auto' : 'none' }}
            >
              <div style={{
                background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px',
                padding: '6px', display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '140px',
                boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
              }}>
                <Link href="/privacy" onClick={e => handleNavClick('/privacy', e, () => setLegalOpen(false))} style={{ color: 'var(--text-muted)', fontSize: '13px', textDecoration: 'none', padding: '7px 10px', borderRadius: '6px', transition: 'background 0.15s' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--surface-raised)'; (e.currentTarget as HTMLElement).style.color = 'var(--text)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)'; }}>
                  Privacy
                </Link>
                <Link href="/terms" onClick={e => handleNavClick('/terms', e, () => setLegalOpen(false))} style={{ color: 'var(--text-muted)', fontSize: '13px', textDecoration: 'none', padding: '7px 10px', borderRadius: '6px', transition: 'background 0.15s' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--surface-raised)'; (e.currentTarget as HTMLElement).style.color = 'var(--text)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)'; }}>
                  Terms
                </Link>
                <Link href="/accessibility" onClick={e => handleNavClick('/accessibility', e, () => setLegalOpen(false))} style={{ color: 'var(--text-muted)', fontSize: '13px', textDecoration: 'none', padding: '7px 10px', borderRadius: '6px', transition: 'background 0.15s' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--surface-raised)'; (e.currentTarget as HTMLElement).style.color = 'var(--text)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)'; }}>
                  Accessibility
                </Link>
              </div>
            </div>
          </div>

          {show && (
            <Link href="/settings" style={navLink}
              onClick={e => handleNavClick('/settings', e)}
              onMouseEnter={e => (e.currentTarget.style.color = 'var(--text)')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted)')}>
              Settings
            </Link>
          )}

          {show && SignalBadge}
          {ThemeToggle}
          <AuthButtons />
        </nav>

        {/* Mobile: badge + hamburger + theme toggle + auth, all always visible.
            flexWrap is a safety net — on the very smallest phones (iPhone SE-class,
            ~375px) this much content might be tight enough to wrap onto a second
            line rather than fit on one. That's an intentional graceful-degradation
            choice over risking an actual overflow/cutoff. */}
        <div className="flex sm:hidden items-center gap-2" style={{ flexWrap: 'wrap', rowGap: '6px' }}>
          <button
            onClick={() => setMenuOpen(o => !o)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            style={{
              background: 'var(--surface-raised)', border: '1px solid var(--border)',
              borderRadius: '8px', width: '34px', height: '34px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--text)', cursor: 'pointer', flexShrink: 0,
            }}
          >
            {menuOpen ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M5 5l14 14M19 5L5 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            )}
          </button>
          {show && SignalBadge}
          {ThemeToggle}
          <AuthButtons />
        </div>
      </div>

      {/* Mobile menu panel */}
      {menuOpen && (
        <>
          <div
            onClick={closeMenu}
            className="sm:hidden"
            style={{ position: 'fixed', inset: 0, top: '57px', background: 'rgba(0,0,0,0.3)', zIndex: 40 }}
          />
          <div
            className="sm:hidden"
            style={{
              position: 'relative', zIndex: 41,
              background: 'var(--surface)', borderBottom: '1px solid var(--border)',
              padding: '4px 20px 16px',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <Link href="/info" style={mobileNavLink} onClick={e => handleNavClick('/info', e, closeMenu)}>How it works</Link>
            <Link href="/demo" style={mobileNavLink} onClick={e => handleNavClick('/demo', e, closeMenu)}>Features</Link>
            <Link href="/methodology" style={mobileNavLink} onClick={e => handleNavClick('/methodology', e, closeMenu)}>Methodology</Link>
            <Link href="/privacy" style={mobileNavLink} onClick={e => handleNavClick('/privacy', e, closeMenu)}>Privacy</Link>
            <Link href="/terms" style={mobileNavLink} onClick={e => handleNavClick('/terms', e, closeMenu)}>Terms</Link>
            <Link href="/accessibility" style={mobileNavLink} onClick={e => handleNavClick('/accessibility', e, closeMenu)}>Accessibility</Link>
            {show && (
              <Link href="/settings" style={mobileNavLink} onClick={e => handleNavClick('/settings', e, closeMenu)}>Settings</Link>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', paddingTop: '14px', gap: '10px' }}>
              {ThemeToggle}
              <AuthButtons />
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes sway {
          0%, 100% { transform: rotate(-3deg); }
          50% { transform: rotate(3deg); }
        }
      `}</style>
    </header>
  );
}
